import { COLOR_CLUSTER_DELTA_E, STROKE_MIN_WIDTH_MM } from '../constants/svgColorAreas';
import { formatHexColor } from './editorUnits';
import {
  compositeOnWhite,
  deltaE,
  parseCssColorToHex,
  parseCssColorToRgb,
  rgbToHex,
  type Rgb,
} from './colorDistance';
import { mapDetectedColorsToOptions, type ColorOptionLike } from './mapColorOptions';
import type { ColorArea, ColorAreaBounds } from '../Reducers/Configurator/types';
import { setElementPaintColor } from './svgColorApply';
import { makeSvgFullyOpaque } from './svgOpacityStrip';
import { expandBBoxForStroke } from './svgStrokeBounds';
import { readStrokeWidthUserUnits, strokeWidthToMm } from './svgStrokeConvert';

const GEOMETRY_TAGS = new Set(['path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon', 'use']);
const NON_RENDERED_ANCESTOR_TAGS = new Set(['defs', 'clippath', 'mask', 'symbol', 'metadata', 'title', 'desc']);
const MEASURE_MOUNT_STYLES =
  'position:absolute;left:-9999px;top:-9999px;overflow:hidden;pointer-events:none';

export type ProcessSvgColorAreasResult = {
  svgSource: string;
  colorAreas: ColorArea[];
  colorAreaBounds: ColorAreaBounds[];
};

export type ProcessSvgColorAreasContext = {
  mmPerUserUnitX: number;
  mmPerUserUnitY: number;
  colorOptions: ColorOptionLike[];
};

function readStyleProperty(style: string, name: string): string | null {
  const match = style.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() ?? null;
}

function resolveUrlId(value: string | null): string | null {
  if (!value) return null;
  const match = value.match(/url\s*\(\s*['"]?#([^'")\s]+)/i);
  return match?.[1] ?? null;
}

function isGeometryTag(el: Element): boolean {
  return GEOMETRY_TAGS.has(el.tagName.toLowerCase());
}

function hasGeometryDescendant(el: Element): boolean {
  return Array.from(el.querySelectorAll('*')).some(isGeometryTag);
}

function isLeafGeometry(el: Element): boolean {
  return isGeometryTag(el) && !hasGeometryDescendant(el);
}

function isMeasurableGeometry(element: Element, root: SVGSVGElement): boolean {
  let node: Element | null = element;
  while (node && node !== root) {
    if (NON_RENDERED_ANCESTOR_TAGS.has(node.tagName.toLowerCase())) return false;
    node = node.parentElement;
  }
  return true;
}

function hasPaintedFill(computed: CSSStyleDeclaration, el: Element): boolean {
  const fill = computed.fill;
  if (fill && fill !== 'none' && fill !== 'transparent') {
    const opacity = parseFloat(computed.fillOpacity);
    if (!Number.isFinite(opacity) || opacity > 0) return true;
  }
  const attrFill = el.getAttribute('fill');
  if (attrFill && attrFill !== 'none' && attrFill !== 'transparent') return true;
  const styleFill = readStyleProperty(el.getAttribute('style') ?? '', 'fill');
  return Boolean(styleFill && styleFill !== 'none' && styleFill !== 'transparent');
}

function hasPaintedStroke(computed: CSSStyleDeclaration, el: Element): boolean {
  const stroke = computed.stroke;
  if (stroke && stroke !== 'none' && stroke !== 'transparent') {
    const opacity = parseFloat(computed.strokeOpacity);
    if (!Number.isFinite(opacity) || opacity > 0) return true;
  }
  const attrStroke = el.getAttribute('stroke');
  if (attrStroke && attrStroke !== 'none' && attrStroke !== 'transparent') return true;
  const styleStroke = readStyleProperty(el.getAttribute('style') ?? '', 'stroke');
  return Boolean(styleStroke && styleStroke !== 'none' && styleStroke !== 'transparent');
}

function isVisible(computed: CSSStyleDeclaration, el: Element): boolean {
  if (computed.display === 'none' || computed.visibility === 'hidden') return false;
  if (el.getAttribute('display') === 'none' || el.getAttribute('visibility') === 'hidden') return false;
  const opacity = parseFloat(computed.opacity);
  return !(Number.isFinite(opacity) && opacity <= 0);
}

function resolveGradientDominantColor(root: SVGSVGElement, value: string): string | null {
  const id = resolveUrlId(value);
  if (!id) return null;
  const gradient = root.querySelector(`#${CSS.escape(id)}`);
  if (!gradient) return null;

  const stops = Array.from(gradient.querySelectorAll('stop'));
  if (stops.length === 0) return null;

  let dominant: { offset: number; color: string } | null = null;
  stops.forEach((stop) => {
    const offset = parseFloat(stop.getAttribute('offset') || '0');
    const normalizedOffset = offset > 1 ? offset / 100 : offset;
    const stopColor =
      stop.getAttribute('stop-color') ||
      readStyleProperty(stop.getAttribute('style') ?? '', 'stop-color') ||
      '#000000';
    const stopOpacityRaw =
      stop.getAttribute('stop-opacity') ||
      readStyleProperty(stop.getAttribute('style') ?? '', 'stop-opacity');
    const stopOpacity = stopOpacityRaw != null ? parseFloat(stopOpacityRaw) : 1;
    const rgb = parseCssColorToRgb(stopColor);
    const hex = rgb ? rgbToHex(compositeOnWhite(rgb, Number.isFinite(stopOpacity) ? stopOpacity : 1)) : null;
    if (!hex) return;
    if (!dominant || normalizedOffset >= dominant.offset) {
      dominant = { offset: normalizedOffset, color: hex };
    }
  });

  return dominant?.color ?? null;
}

function resolvePatternDominantColor(root: SVGSVGElement, value: string): string | null {
  const id = resolveUrlId(value);
  if (!id) return null;
  const pattern = root.querySelector(`#${CSS.escape(id)}`);
  if (!pattern) return null;

  const painted = pattern.querySelector('[fill],[stroke]');
  if (!painted) return null;
  const fill = painted.getAttribute('fill');
  const stroke = painted.getAttribute('stroke');
  return parseCssColorToHex(fill || stroke);
}

function resolvePaintColor(
  root: SVGSVGElement,
  computed: CSSStyleDeclaration,
  rawValue: string | null | undefined,
  opacityValue: string
): string | null {
  if (!rawValue || rawValue === 'none' || rawValue === 'transparent') return null;

  if (rawValue.includes('url(')) {
    const gradientColor = resolveGradientDominantColor(root, rawValue);
    if (gradientColor) return formatHexColor(gradientColor);
    const patternColor = resolvePatternDominantColor(root, rawValue);
    if (patternColor) return formatHexColor(patternColor);
    return null;
  }

  const opacity = parseFloat(opacityValue);
  const rgb = parseCssColorToRgb(rawValue);
  if (!rgb) return null;
  const alpha = Number.isFinite(opacity) ? opacity : 1;
  return formatHexColor(rgbToHex(compositeOnWhite(rgb, alpha)));
}

function resolveElementFillColor(root: SVGSVGElement, computed: CSSStyleDeclaration): string | null {
  return resolvePaintColor(root, computed, computed.fill, computed.fillOpacity);
}

function resolveElementStrokeColor(root: SVGSVGElement, computed: CSSStyleDeclaration): string | null {
  return resolvePaintColor(root, computed, computed.stroke, computed.strokeOpacity);
}

function removeStrokeAttributes(el: Element): void {
  el.removeAttribute('stroke');
  el.removeAttribute('stroke-width');
  el.removeAttribute('stroke-opacity');
  el.removeAttribute('stroke-linecap');
  el.removeAttribute('stroke-linejoin');
  el.removeAttribute('stroke-miterlimit');
  el.removeAttribute('stroke-dasharray');
  el.removeAttribute('stroke-dashoffset');

  const style = el.getAttribute('style');
  if (!style) return;
  const cleaned = style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !/^stroke(?:-|$)/i.test(part.split(':')[0]?.trim() || ''))
    .join('; ');
  if (cleaned) el.setAttribute('style', cleaned);
  else el.removeAttribute('style');
}

function boundsFromDomRect(rect: DOMRect): ColorAreaBounds {
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
}

type LeafColorEntry = {
  element: Element;
  color: string;
  bounds: ColorAreaBounds;
  paint: 'fill' | 'stroke';
  /** Fill and stroke share the same color on this element. */
  linkedStroke?: boolean;
};

function clusterColors(detectedHexes: string[]): string[] {
  const clusters: { representative: Rgb; hex: string }[] = [];

  detectedHexes.forEach((hex) => {
    const rgb = parseCssColorToRgb(hex);
    if (!rgb) return;

    const existing = clusters.find((cluster) => deltaE(cluster.representative, rgb) < COLOR_CLUSTER_DELTA_E);
    if (existing) return;
    clusters.push({ representative: rgb, hex: formatHexColor(hex) });
  });

  return clusters.map((cluster) => cluster.hex);
}

function findClusterIndex(color: string, clusteredHexes: string[]): number {
  const rgb = parseCssColorToRgb(color);
  if (!rgb || clusteredHexes.length === 0) return 0;

  let bestIndex = 0;
  let bestDistance = Infinity;
  clusteredHexes.forEach((hex, index) => {
    const clusterRgb = parseCssColorToRgb(hex);
    if (!clusterRgb) return;
    const distance = deltaE(rgb, clusterRgb);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

function mergeBounds(a: ColorAreaBounds, b: ColorAreaBounds): ColorAreaBounds {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x + a.width, b.x + b.width);
  const maxY = Math.max(a.y + a.height, b.y + b.height);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function withMountedSvgRoot<T>(doc: Document, measure: (root: SVGSVGElement) => T): T | null {
  const parsedRoot = doc.documentElement;
  if (!parsedRoot || parsedRoot.nodeName.toLowerCase() !== 'svg') return null;

  const root = parsedRoot.cloneNode(true) as SVGSVGElement;
  root.style.cssText = MEASURE_MOUNT_STYLES;
  document.body.appendChild(root);

  try {
    return measure(root);
  } finally {
    document.body.removeChild(root);
  }
}

function removeThinStrokes(
  root: SVGSVGElement,
  mmPerUserUnitX: number,
  mmPerUserUnitY: number
): void {
  Array.from(root.querySelectorAll('*'))
    .filter(isGeometryTag)
    .forEach((element) => {
      if (!isMeasurableGeometry(element, root) || !isVisible(getComputedStyle(element), element)) {
        return;
      }
      const computed = getComputedStyle(element);
      if (!hasPaintedStroke(computed, element)) return;

      const strokeWidthUser = readStrokeWidthUserUnits(computed, element);
      const strokeWidthMm = strokeWidthToMm(strokeWidthUser, mmPerUserUnitX, mmPerUserUnitY);
      if (strokeWidthMm >= STROKE_MIN_WIDTH_MM) return;

      removeStrokeAttributes(element);
    });
}

function collectLeafColors(root: SVGSVGElement): LeafColorEntry[] {
  const entries: LeafColorEntry[] = [];

  Array.from(root.querySelectorAll('*'))
    .filter(isGeometryTag)
    .forEach((element) => {
      if (!isLeafGeometry(element) || !isMeasurableGeometry(element, root)) return;
      const computed = getComputedStyle(element);
      if (!isVisible(computed, element)) return;

      const geom = element as SVGGraphicsElement;
      let bbox: DOMRect;
      try {
        bbox = geom.getBBox();
      } catch {
        return;
      }

      const hasFill = hasPaintedFill(computed, element);
      const hasStroke = hasPaintedStroke(computed, element);
      const fillColor = hasFill ? resolveElementFillColor(root, computed) : null;
      const strokeColor = hasStroke ? resolveElementStrokeColor(root, computed) : null;

      if (!fillColor && !strokeColor) return;

      const fillBounds =
        bbox.width > 0 || bbox.height > 0
          ? { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height }
          : null;

      const sameAsFill = Boolean(
        fillColor &&
          strokeColor &&
          formatHexColor(fillColor) === formatHexColor(strokeColor) &&
          fillBounds &&
          fillBounds.width > 0 &&
          fillBounds.height > 0
      );

      if (fillColor && fillBounds && fillBounds.width > 0 && fillBounds.height > 0) {
        const fillRect = new DOMRect(fillBounds.x, fillBounds.y, fillBounds.width, fillBounds.height);
        const expandedFill =
          sameAsFill && hasStroke
            ? expandBBoxForStroke(geom, fillRect, computed, true)
            : fillRect;

        entries.push({
          element,
          color: fillColor,
          bounds: boundsFromDomRect(expandedFill),
          paint: 'fill',
          linkedStroke: sameAsFill && hasStroke,
        });
      }

      if (strokeColor) {
        const strokeBBox = expandBBoxForStroke(geom, bbox, computed, true);
        if (!(strokeBBox.width > 0) || !(strokeBBox.height > 0)) return;

        if (!sameAsFill) {
          entries.push({
            element,
            color: strokeColor,
            bounds: boundsFromDomRect(strokeBBox),
            paint: 'stroke',
          });
        }
      }
    });

  return entries;
}

function serializeSvg(root: SVGSVGElement): string {
  return new XMLSerializer().serializeToString(root);
}

export function processSvgColorAreas(
  rawSvg: string,
  context: ProcessSvgColorAreasContext
): ProcessSvgColorAreasResult | null {
  if (!rawSvg?.trim()) return null;

  const doc = new DOMParser().parseFromString(rawSvg, 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;

  const result = withMountedSvgRoot(doc, (root) => {
    makeSvgFullyOpaque({
      root,
      isGeometry: isGeometryTag,
      isMeasurable: (element) => isMeasurableGeometry(element, root),
      isVisible,
      hasPaintedFill,
      hasPaintedStroke,
      resolveFillColor: (computed) => resolveElementFillColor(root, computed),
      resolveStrokeColor: (computed) => resolveElementStrokeColor(root, computed),
      removeStroke: removeStrokeAttributes,
    });

    removeThinStrokes(root, context.mmPerUserUnitX, context.mmPerUserUnitY);

    const leafEntries = collectLeafColors(root);
    const clusteredHexes = clusterColors(leafEntries.map((entry) => entry.color));
    const colorAreas = mapDetectedColorsToOptions(clusteredHexes, context.colorOptions);

    const colorAreaBounds: ColorAreaBounds[] = colorAreas.map(() => ({
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    }));

    leafEntries.forEach(({ element, color, bounds, paint, linkedStroke }) => {
      const areaIndex = findClusterIndex(color, clusteredHexes);
      const mappedColor = colorAreas[areaIndex]?.color;
      if (!mappedColor) return;

      if (paint === 'stroke') {
        const fillPaint = element.getAttribute('data-color-paint');
        const hasFillArea =
          element.hasAttribute('data-color-area') &&
          fillPaint !== 'stroke' &&
          fillPaint !== 'both';
        if (hasFillArea) {
          element.setAttribute('data-stroke-color-area', String(areaIndex));
        } else {
          element.setAttribute('data-color-area', String(areaIndex));
          element.setAttribute('data-color-paint', 'stroke');
        }
        setElementPaintColor(element, 'stroke', mappedColor);
      } else {
        element.setAttribute('data-color-area', String(areaIndex));
        if (linkedStroke) {
          element.setAttribute('data-color-paint', 'both');
          setElementPaintColor(element, 'fill', mappedColor);
          setElementPaintColor(element, 'stroke', mappedColor);
        } else {
          element.removeAttribute('data-color-paint');
          setElementPaintColor(element, 'fill', mappedColor);

          const strokeAreaIndex = element.getAttribute('data-stroke-color-area');
          if (strokeAreaIndex != null) {
            const separateStrokeColor = colorAreas[Number(strokeAreaIndex)]?.color;
            if (separateStrokeColor) setElementPaintColor(element, 'stroke', separateStrokeColor);
          }
        }
      }

      const currentBounds = colorAreaBounds[areaIndex];
      if (currentBounds.width <= 0 || currentBounds.height <= 0) {
        colorAreaBounds[areaIndex] = { ...bounds };
      } else {
        colorAreaBounds[areaIndex] = mergeBounds(currentBounds, bounds);
      }
    });

    return {
      svgSource: serializeSvg(root),
      colorAreas,
      colorAreaBounds,
    };
  });

  return result;
}
